/**
 * Calculates OI (Open Interest) analysis based on price and OI movements
 *
 * Analysis types:
 * - Long build: Price ↑, OI ↑ (Bullish accumulation)
 * - Short build: Price ↓, OI ↑ (Bearish accumulation)
 * - Long unwind: Price ↓, OI ↓ (Long profit booking)
 * - Short cover: Price ↑, OI ↓ (Short covering)
 *
 * @param {string|number} percentageChange - Percentage change in price (from symbolData.change_per)
 * @param {string|number} currentOi - Current Open Interest
 * @param {string|number} previousOi - Previous Open Interest
 * @param {string|number} currentLtp - Current LTP (Last Traded Price)
 * @param {string|number} prevClose - Previous close price
 * @returns {string|null} Analysis string or null if cannot determine
 */
export function getOIAnalysis(percentageChange, currentOi, previousOi, currentLtp, prevClose) {
  // Parse values
  const oiValue = parseFloat(currentOi);
  const previousOiValue = parseFloat(previousOi);

  // Need valid current and previous OI
  if (!isFinite(oiValue) || !isFinite(previousOiValue)) {
    return null;
  }

  // Determine if OI changed
  const oiUp = oiValue > previousOiValue;
  const oiDown = oiValue < previousOiValue;

  // If OI didn't change, no analysis
  if (!oiUp && !oiDown) {
    return null;
  }

  // Determine price direction
  let priceUp = false;
  let priceDown = false;

  // Try to use percentage change first
  if (percentageChange && percentageChange !== '--' && percentageChange !== '0') {
    const pcValue = parseFloat(percentageChange);
    if (isFinite(pcValue) && pcValue !== 0.0) {
      priceUp = pcValue > 0;
      priceDown = pcValue < 0;
    }
  }

  // If no price direction yet, try LTP vs prevClose
  if (!priceUp && !priceDown) {
    const ltpValue = parseFloat(currentLtp);
    const prevCloseValue = parseFloat(prevClose);

    if (isFinite(ltpValue) && isFinite(prevCloseValue) && prevCloseValue > 0.0) {
      const diff = ltpValue - prevCloseValue;
      if (Math.abs(diff) > 0.01) {  // More than 1 paisa difference
        priceUp = diff > 0;
        priceDown = diff < 0;
      }
    }
  }

  // If still can't determine price direction, return null
  if (!priceUp && !priceDown) {
    return null;
  }

  // Apply OI analysis logic
  if (priceUp && oiUp) {
    return 'Long build';
  } else if (priceDown && oiUp) {
    return 'Short build';
  } else if (priceDown && oiDown) {
    return 'Long unwind';
  } else if (priceUp && oiDown) {
    return 'Short cover';
  }

  return null;
}
